import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, CheckCircle2, User, Mail, Phone, Languages, Briefcase, ShieldCheck, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";
import { trackEvent } from "@/lib/analytics";

const LANGUAGES = [
    "Deutsch", "English", "Türkçe", "Русский", "العربية", "हिन्दी", "Español", "Français", "Shqip", "Italiano", "Polski", "Português", "Română",
];

const LEAD_TESTID = "lead-form";

export default function LeadFormPage() {
    const [form, setForm] = useState({
        firstname: "",
        lastname: "",
        email: "",
        phone: "",
        language: "Deutsch",
        profession: "",
    });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [done, setDone] = useState(false);
    const [mailSent, setMailSent] = useState(true);

    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        // Basic client check — every field required
        for (const k of ["firstname", "lastname", "email", "phone", "language", "profession"]) {
            if (!String(form[k] || "").trim()) {
                setError("Bitte fülle alle Felder aus.");
                return;
            }
        }
        setBusy(true);
        try {
            const { data } = await api.post("/public/leads", { ...form, source: "qr" });
            trackEvent("generate_lead", { lead_source: "qr" });
            setMailSent(!!data?.mail_sent);
            setDone(true);
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || "Es ist ein Fehler aufgetreten. Bitte erneut versuchen.");
        } finally {
            setBusy(false);
        }
    };

    if (done) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-red-50/40 to-amber-50/30 flex items-center justify-center p-5">
                <div className="w-full max-w-md bg-white rounded-3xl shadow-xl p-8 text-center border border-slate-100" data-testid="lead-form-success">
                    <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mb-5">
                        <CheckCircle2 className="text-emerald-600" size={32} />
                    </div>
                    <h1 className="font-heading text-2xl font-bold text-slate-900">Danke, {form.firstname}!</h1>
                    <p className="mt-3 text-slate-600 leading-relaxed">
                        Wir haben dich für <strong>7 Tage kostenlos</strong> freigeschaltet.
                        {mailSent ? (
                            <> Wir haben dir gerade eine E-Mail an <strong>{form.email}</strong> geschickt
                            mit deinem persönlichen Login-Link. Schau auch in den Spam-Ordner, falls die Mail nicht kommt.</>
                        ) : (
                            <> Unser Team meldet sich kurz bei dir, um den Zugang manuell zu aktivieren.</>
                        )}
                    </p>
                    <Link
                        to="/"
                        data-testid="lead-form-back-home"
                        className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold btn-primary-grad"
                    >
                        Zur Startseite
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-red-50/40 to-amber-50/30 px-4 sm:px-6 py-8 sm:py-12">
            <Seo title="Schnell-Kontakt | Bewerbung24.ai" description="Hinterlasse deine Kontaktdaten — wir melden uns und schalten deinen Testzugang frei." path="/lead" noindex />
            <div className="max-w-lg mx-auto">
                <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4" data-testid="lead-form-home-link">
                    <ArrowLeft size={14} /> Zur Website
                </Link>

                <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
                    <div className="px-6 sm:px-8 pt-7 pb-5 border-b border-slate-100" style={{ background: "linear-gradient(35deg, rgba(231,52,63,0.08), rgba(249,176,0,0.08))" }}>
                        <div className="flex items-center gap-2">
                            <span className="b24-logo-shine"><img src="/logo-icon.png" alt="bewerbung24.ai" className="h-9 w-auto" /></span>
                            <span className="font-heading font-bold"><span className="b24-shine-dark">Bewerbung24</span><span className="b24-shine-red">.ai</span></span>
                        </div>
                        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mt-4 leading-tight">
                            Schnell-Kontakt
                        </h1>
                        <p className="text-sm text-slate-600 mt-1.5">
                            Trag deine Daten ein — wir melden uns persönlich bei dir.
                        </p>
                    </div>

                    <form onSubmit={submit} data-testid={LEAD_TESTID} className="px-6 sm:px-8 py-6 space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <Field
                                icon={User}
                                label="Vorname"
                                testid="lead-firstname"
                                value={form.firstname}
                                onChange={(v) => update("firstname", v)}
                                required
                                autoComplete="given-name"
                            />
                            <Field
                                icon={User}
                                label="Nachname"
                                testid="lead-lastname"
                                value={form.lastname}
                                onChange={(v) => update("lastname", v)}
                                required
                                autoComplete="family-name"
                            />
                        </div>
                        <Field
                            icon={Mail}
                            label="E-Mail-Adresse"
                            type="email"
                            testid="lead-email"
                            value={form.email}
                            onChange={(v) => update("email", v)}
                            required
                            autoComplete="email"
                            inputMode="email"
                        />
                        <Field
                            icon={Phone}
                            label="Handynummer"
                            type="tel"
                            testid="lead-phone"
                            value={form.phone}
                            onChange={(v) => update("phone", v)}
                            required
                            autoComplete="tel"
                            inputMode="tel"
                            placeholder="+49 170 1234567"
                        />
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                                <Languages className="inline mr-1.5 -mt-0.5" size={13} /> Sprache
                            </label>
                            <select
                                data-testid="lead-language"
                                value={form.language}
                                onChange={(e) => update("language", e.target.value)}
                                className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-red-500 outline-none text-sm font-medium text-slate-800"
                            >
                                {LANGUAGES.map((l) => (
                                    <option key={l} value={l}>{l}</option>
                                ))}
                            </select>
                        </div>
                        <Field
                            icon={Briefcase}
                            label="Aktueller Beruf"
                            testid="lead-profession"
                            value={form.profession}
                            onChange={(v) => update("profession", v)}
                            required
                            placeholder="z.B. Mechatroniker, Pflegekraft, Student..."
                        />

                        {error && (
                            <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="lead-form-error">{error}</p>
                        )}

                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="lead-form-submit"
                            className="w-full py-3.5 rounded-full font-semibold text-white btn-primary-grad inline-flex items-center justify-center gap-2 disabled:opacity-60"
                        >
                            {busy ? <Loader2 className="animate-spin" size={16} /> : null}
                            {busy ? "Wird gesendet..." : "Daten absenden"}
                        </button>

                        <p className="text-[11px] text-slate-500 flex items-start gap-1.5 leading-relaxed pt-1">
                            <ShieldCheck size={13} className="text-emerald-600 shrink-0 mt-0.5" />
                            Deine Daten werden ausschließlich für die Rückmeldung durch Bewerbung24.ai genutzt. Mehr dazu in der{" "}
                            <Link to="/datenschutz" className="underline hover:text-slate-700">Datenschutzerklärung</Link>.
                        </p>
                    </form>
                </div>
            </div>
        </div>
    );
}

function Field({ icon: Icon, label, testid, value, onChange, type = "text", required, autoComplete, inputMode, placeholder }) {
    return (
        <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                {Icon && <Icon className="inline mr-1.5 -mt-0.5" size={13} />} {label}{required && <span className="text-red-500 ml-0.5">*</span>}
            </label>
            <input
                data-testid={testid}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                required={required}
                autoComplete={autoComplete}
                inputMode={inputMode}
                placeholder={placeholder}
                className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-red-500 outline-none text-sm"
            />
        </div>
    );
}
